'use client';

import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { PlayCircle } from 'lucide-react';
import Image from 'next/image';

interface VideoPreviewProps {
  url: string;
  poster?: string;
  className?: string;
  priority?: boolean;
}

/**
 * VideoPreview Component - Elite Zero-Waste Implementation
 * Replaces autoplay/stop logic with "Load-on-Intent".
 * Data is only downloaded when the user explicitly clicks the play button.
 */
export function VideoPreview({ url, poster, className, priority }: VideoPreviewProps) {
  const [isLoaded, setIsLoaded] = useState(false);

  const handlePlay = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoaded(true);
  };

  return (
    <div className={cn("relative w-full h-full overflow-hidden bg-black flex items-center justify-center group", className)}>
      {!isLoaded ? (
        <>
          {poster ? (
            <Image 
              src={poster} 
              alt="Video thumbnail" 
              fill 
              sizes="(max-width: 768px) 100vw, 400px"
              className="object-cover opacity-75 transition-opacity duration-500 group-hover:opacity-90"
              priority={priority}
              fetchPriority={priority ? "high" : "auto"}
            />
          ) : (
            <div className="absolute inset-0 bg-zinc-900 flex items-center justify-center">
               <div className="text-zinc-700 opacity-20 transform scale-[3]">
                  <PlayCircle strokeWidth={1} className="h-24 w-24" />
               </div>
            </div>
          )}
          
          <div 
            className="absolute inset-0 flex items-center justify-center cursor-pointer z-20 transition-colors"
            onClick={handlePlay}
          >
            <div className="h-14 w-14 rounded-full bg-primary/90 flex items-center justify-center shadow-2xl transition-transform group-hover:scale-110">
              <PlayCircle className="text-white h-7 w-7" />
            </div>
          </div>
          
          <div className="absolute bottom-3 right-3 bg-black/60 px-2 py-1 rounded text-[8px] font-black uppercase tracking-widest text-white/80 pointer-events-none">
            Click to Play
          </div>
        </>
      ) : (
        <video
          autoPlay
          controls
          src={url}
          className="w-full h-full object-cover"
          playsInline
        >
          <source src={url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
}
